declare global {
	interface Window {
		flxConsent?: ConsentApi;
	}
}

class Cookies {
	static get(name: string): string | false {
		const cookie_name = `${name}=`;
		const decodedCookie = decodeURIComponent(document.cookie);
		const ca = decodedCookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') {
				c = c.substring(1);
			}
			if (c.indexOf(cookie_name) === 0) {
				return c.substring(cookie_name.length, c.length);
			}
		}
		return false;
	}

	static set(name: string, value: string, expiry_days: number): string | false {
		const d = new Date();
		d.setTime(d.getTime() + expiry_days * 24 * 60 * 60 * 1000);
		const expires = `expires=${d.toUTCString()}`;
		document.cookie = `${name}=${value};${expires};path=/;SameSite=lax`;
		return this.get(name);
	}

	static delete(name: string): void {
		this.set(name, '', 0);
	}
}

const defaults = {
	endpoint: '/api/consent',
	cookieName: 'privacy-consent-prefs',
	cookieExpiry: 180,
	attachToWindow: true,
	disabled: false,
};

type Option = {
	description: string | null;
	label: string | null;
	optIn: boolean;
	value: string;
};

export type Options = {
	// TODO: RENAME TO ConsentConfig or something more understandable
	cookieName: string;
	text: Record<string, string>;
	vrsion: number | null;
	options: Option[];
};

type ConsentPayload = Record<string, boolean>;

export class ConsentApi {
	listeners: (() => void)[] = [];

	consent: ConsentPayload | null = null;

	options: Options | null = null;

	attachToWindow = true;

	disabled = false;

	endpoint: string = '';
	cookieName: string = '';
	cookieExpiry: number = 0;

	constructor(config = {}) {
		const { endpoint, cookieName, cookieExpiry, attachToWindow, disabled } = { ...defaults, ...config };
		this.endpoint = endpoint;
		this.cookieName = cookieName;
		this.cookieExpiry = cookieExpiry;
		this.attachToWindow = attachToWindow;
		this.disabled = disabled;

		// Avoiding Javascript doing Javascripty stuff... grrr.
		this.init = this.init.bind(this);
		this.subscribe = this.subscribe.bind(this);
		this.allows = this.allows.bind(this);
		this.giveConsent = this.giveConsent.bind(this);
		this.revokeConsent = this.revokeConsent.bind(this);
		this.setConsent = this.setConsent.bind(this);
		this.getConsent = this.getConsent.bind(this);
		this.getOptions = this.getOptions.bind(this);
		this.init();
	}

	init(): void {
		if (!window.flxConsent) {
			window.flxConsent = this;
		} else {
			delete window.flxConsent;
		}

		const data = this.getConsent();
		this.consent = data;
		this.notify();
	}

	subscribe(fn: () => void): () => void {
		this.listeners = [...this.listeners, fn];
		return () => {
			this.listeners = this.listeners.filter((l) => l !== fn);
		};
	}

	notify(): void {
		this.listeners.forEach((l) => l());
	}

	allows(name: string): boolean {
		if (this.disabled) {
			return true;
		}
		if (this.consent === null) {
			return false;
		}
		return this.consent[name];
	}

	async giveConsent(answers?: ConsentPayload): Promise<void> {
		console.log('give', answers);
		const given = answers ?? {};
		const data = await this.getOptions();
		if (!data) return;
		const { options } = data;
		if (!options) {
			return;
		}

		const result = options.reduce((prev: ConsentPayload, curr: Option) => {
			let value = true;
			if (curr.optIn && given[curr.value] !== undefined) {
				value = given[curr.value];
			}
			return { ...prev, [curr.value]: value };
		}, {});

		this.setConsent(result);
	}

	revokeConsent(): void {
		Cookies.delete(this.cookieName);

		this.consent = null;

		this.notify();
	}

	setConsent(payload: ConsentPayload): void {
		Cookies.set(this.cookieName, JSON.stringify(payload), this.cookieExpiry);

		this.consent = payload;

		this.notify();
	}

	getConsent(): ConsentPayload | null {
		const raw = Cookies.get(this.cookieName);
		return raw ? JSON.parse(raw) : null;
	}

	get isConsentPending(): boolean {
		if (this.disabled) {
			return false;
		}
		return this.consent === null;
	}

	optionCallbacks: ((value: Options | null) => void)[] = [];
	isFetching = false;
	getOptions(): Promise<Options | null> {
		if (this.options !== null) {
			return Promise.resolve(this.options);
		}
		if (this.isFetching) {
			return new Promise((resolve) => this.optionCallbacks.push(resolve));
		}
		this.isFetching = true;
		try {
			return fetch(this.endpoint)
				.then((res) => res.json())
				.then((options: Options) => {
					this.options = options;
					this.optionCallbacks.forEach((cb) => cb(options));
					this.optionCallbacks = [];
					return options;
				});
		} catch (err) {
			console.error(err);
			return Promise.resolve(null);
		}
	}
}
